/**
 * MyScorecardModal.jsx  (Oct 6, 2026)
 * ============================================================================
 * "Where do I see my scores?" — the player's own hole-by-hole card for one
 * round, after the round as well as during it.
 *
 * Opened from two places:
 *   - the tee time's Results / Settlement / Scorecard row in the player portal;
 *   - a tap on a round in Stats → Rounds.
 *
 * Fetches GET /api/player/scoring/my-card (only the viewer's own row) and hands
 * it to PlayerScorecardModal, the same card the leaderboards open, so there is
 * one card design in the app. While loading, or when there is no card, it shows
 * a small box of its own.
 *
 * LINE ENDINGS: LF, matching its siblings in this folder.
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import PlayerScorecardModal from './PlayerScorecardModal';

// apiUrl comes from the caller (App.js and ScoringStats.jsx each define their own),
// so this component never guesses the API host.
const MyScorecardModal = ({ apiUrl, token, teetimeId, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const resp = await fetch(`${apiUrl}/player/scoring/my-card?token=${encodeURIComponent(token)}&teetime_id=${encodeURIComponent(teetimeId)}`);
        const body = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok) setError(body.error || `Server error (${resp.status})`);
        else setData(body);
      } catch (e) {
        if (!cancelled) setError('Network error — check your connection');
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, token, teetimeId]);

  if (data && data.player) {
    const board = data.board || {};
    return (
      <PlayerScorecardModal
        player={data.player}
        board={board}
        hideNet={board.scoring_mode === 'team' && board.has_net !== true}
        onClose={onClose}
      />
    );
  }

  const message = error
    ? error
    : data
      ? 'No scores on your card for this round.'
      : 'Loading your scorecard…';

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, zIndex: 1000 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 12, padding: '22px 20px', width: '100%', maxWidth: 360, textAlign: 'center', fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
      >
        <div style={{ fontSize: 15, color: error ? '#b91c1c' : '#374151', marginBottom: 16 }}>{message}</div>
        <button
          onClick={onClose}
          style={{ padding: '10px 22px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 15, fontWeight: 600, color: '#374151', cursor: 'pointer' }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default MyScorecardModal;
